import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BaseAssessment,
  CreateAssessment,
  UpdateAssessment,
} from '../dtos/Assessments/index';
import { RegistryTermsResponse } from '../dtos/AssessmentParameters/index';
import { AssessmentRule } from '../dtos/Rules/index';

class AssessmentApi extends BaseApi {
  private readonly basePath = '/assessments';

  /**
   * Get registry terms (term keys and definitions for assessment items)
   * GET /api/assessments/registry-terms
   */
  async getRegistryTerms(): Promise<Response<RegistryTermsResponse | null>> {
    return this.get<RegistryTermsResponse | null>(`${this.basePath}/registry-terms`);
  }

  /**
   * Get assessment display rules
   * GET /api/assessments/rules
   */
  async getRules(): Promise<Response<Record<string, AssessmentRule>>> {
    return this.get<Record<string, AssessmentRule>>(`${this.basePath}/rules`);
  }

  /**
   * Get assessment by ID
   * GET /api/assessments/{id}
   */
  async getById(id: string): Promise<Response<BaseAssessment | null>> {
    return this.get<BaseAssessment | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all assessments
   * GET /api/assessments
   */
  async getList(): Promise<Response<BaseAssessment[]>> {
    return this.get<BaseAssessment[]>(this.basePath);
  }

  /**
   * Get paginated list of assessments with optional search
   * GET /api/assessments/search?searchTerm=&pageNumber=1&pageSize=10 (when search is provided)
   * GET /api/assessments/paged?pageNumber=1&pageSize=10 (when no search)
   * Searches: Name, Specialization
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BaseAssessment>>> {
    if (searchTerm) {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      return this.get<PagedResponse<BaseAssessment>>(
        `${this.basePath}/search?${params.toString()}`
      );
    }
    
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BaseAssessment>>(
      `${this.basePath}/paged?${params.toString()}`
    );
  }

  /**
   * Create a new assessment
   * POST /api/assessments
   */
  async create(data: CreateAssessment): Promise<Response<BaseAssessment>> {
    return this.post<BaseAssessment, CreateAssessment>(this.basePath, data);
  }

  /**
   * Update an existing assessment
   * PUT /api/assessments/{id}
   */
  async update(id: string, data: Omit<UpdateAssessment, 'id'>): Promise<Response<BaseAssessment>> {
    return this.put<BaseAssessment, UpdateAssessment>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete an assessment
   * DELETE /api/assessments/{id}
   */
  async deleteAssessment(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const assessmentApi = new AssessmentApi();

export default AssessmentApi;
